/**
 * useEntitySelection Hook
 * 
 * Custom hook for managing entity selection state with pagination and search
 * Reusable logic for selecting entities from a paginated list
 * 
 * @example
 * // Basic usage
 * const userSelection = useEntitySelection({
 *   fetchFn: userService.getUsers,
 *   queryKey: 'users-selection',
 * });
 * 
 * @example
 * // Agent selection with hostname search
 * const agentSelection = useEntitySelection({
 *   fetchFn: agentService.getAgents,
 *   queryKey: 'agents-selection',
 *   searchField: 'hostname', // Search specifically by hostname
 * });
 * 
 * @example
 * // Certificate selection with name search
 * const certificateSelection = useEntitySelection({
 *   fetchFn: certificateService.getCertificates,
 *   queryKey: 'certificates-selection',
 *   searchField: 'name',
 * });
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PaginationParams, PaginatedResponse } from '@/types';

interface UseEntitySelectionOptions<T> {
  fetchFn: (params: PaginationParams) => Promise<PaginatedResponse<T>>;
  queryKey: string;
  initialPageSize?: number;
  filters?: any; // Additional filters (not currently used in standard implementation)
  searchField?: string; // Field to search on (e.g., 'hostname', 'name', 'identifier')
  searchPlaceholder?: string; // Custom placeholder for search input
}

interface UseEntitySelectionReturn<T> {
  // Data
  entities: T[];
  selectedIds: string[];
  
  // Pagination
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  
  // State
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  searchField?: string;
  searchPlaceholder?: string;
  
  // Actions
  setSelectedIds: (ids: string[]) => void;
  handleSearch: (term: string) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  clearSelection: () => void;
  refetch: () => void;
  
  // Filter info
  hasActiveFilters: boolean;
  activeFiltersCount: number;
}

/**
 * Hook for managing entity selection
 */
export function useEntitySelection<T extends { id: string }>({
  fetchFn,
  queryKey,
  initialPageSize = 10,
  searchField,
  searchPlaceholder,
}: UseEntitySelectionOptions<T>): UseEntitySelectionReturn<T> {
  // State
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  /**
   * Debounce search term to avoid excessive API calls
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Reduced debounce time for better UX

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  /**
   * Memoize query parameters to prevent unnecessary re-renders
   * Use specific field names (e.g. 'hostname') instead of generic 'search'
   */
  const memoizedParams = useMemo(() => {
    const baseParams: PaginationParams = {
      page: currentPage - 1, // API is 0-indexed
      size: pageSize,
    };

    // Add search term with the correct field name
    if (searchField && debouncedSearchTerm) {
      if (searchField === 'hostname') {
        // API expects 'hostname' parameter for hostname search
        (baseParams as any).hostname = debouncedSearchTerm;
      } else {
        // For other fields, use generic 'search'
        baseParams.search = debouncedSearchTerm;
      }
    }

    return baseParams;
  }, [currentPage, pageSize, searchField, debouncedSearchTerm]);

  /**
   * Create stable query key
   */
  const stableQueryKey = useMemo(() => [
    queryKey,
    currentPage,
    pageSize,
    searchField,
    debouncedSearchTerm,
  ], [queryKey, currentPage, pageSize, searchField, debouncedSearchTerm]);

  /**
   * Fetch entities with pagination and search
   */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: stableQueryKey,
    queryFn: async () => {
      return fetchFn(memoizedParams);
    },
    enabled: true, // Always enabled, but use stable keys to prevent excessive calls
    staleTime: 1000 * 60 * 2, // 2 minutes - reasonable for entity selection data
    gcTime: 1000 * 60 * 5, // 5 minutes cache retention
    refetchOnWindowFocus: false, // Prevent refetch on window focus for better UX
    retry: 2, // Limit retries for failed requests
  });

  /**
   * Handle search
   */
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page on page size change
  }, []);

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Calculate active filters info
  const hasActiveFilters = useMemo(() => {  
    return !!debouncedSearchTerm;
  }, [debouncedSearchTerm]);
    
  const activeFiltersCount = useMemo(() => {
    return debouncedSearchTerm ? 1 : 0;
  }, [debouncedSearchTerm]);

  return {
    // Data
    entities: data?.data || [],
    selectedIds,
    
    // Pagination
    currentPage,
    totalPages: data?.pagination?.totalPages || 1,
    pageSize,
    totalItems: data?.pagination?.total || 0,
    
    // State
    isLoading,
    error: error ? (error as Error).message : null,
    searchTerm,
    searchField,
    searchPlaceholder: searchPlaceholder || `Buscar ${searchField ? `por ${searchField}` : ''}...`,
    
    // Actions
    setSelectedIds,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    clearSelection,
    refetch,
    
    // Filter info
    hasActiveFilters,
    activeFiltersCount,
  };
}

export default useEntitySelection;
